import type { ReactNode } from "react";
import { Icon, type IconName } from "./Icon.tsx";

export interface RowProps {
  icon?: IconName | ReactNode;
  iconBg?: string;
  title: ReactNode;
  sub?: ReactNode;
  val?: ReactNode;
  valColor?: string;
  right?: ReactNode;
  onClick?: () => void;
  chevron?: boolean;
}

/** Linha de lista estilo iOS. */
export function Row({ icon, iconBg, title, sub, val, valColor, right, onClick, chevron = true }: RowProps) {
  return (
    <button
      type="button"
      className="m-row"
      onClick={onClick}
      style={onClick ? undefined : { cursor: "default" }}
    >
      {icon != null && (
        <span className="r-ic" style={{ background: iconBg ?? "var(--primary)" }}>
          {typeof icon === "string" ? <Icon name={icon as IconName} size={18} color="#fff" /> : icon}
        </span>
      )}
      <span className="r-main">
        <span className="r-title">{title}</span>
        {sub != null && <span className="r-sub">{sub}</span>}
      </span>
      {val != null && (
        <span className="r-val" style={valColor ? { color: valColor } : undefined}>
          {val}
        </span>
      )}
      {right}
      {onClick && chevron && (
        <svg
          className="m-chev"
          viewBox="0 0 8 13"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M1 1l6 6-6 6" />
        </svg>
      )}
    </button>
  );
}

export interface SheetProps {
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
  /** `false` quando há digitação a perder: tocar fora não fecha (só o ✕). Padrão: `true`. */
  closeOnScrim?: boolean;
}

/** Bottom sheet para edição focada. */
export function Sheet({ title, sub, onClose, children, foot, closeOnScrim = true }: SheetProps) {
  return (
    <div
      className="m-sheet-scrim"
      role="presentation"
      onClick={(e) => {
        if (closeOnScrim && e.target === e.currentTarget) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          onClose();
        }
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: bottom-sheet customizado (scrim/animação/posição próprios); <dialog> nativo conflita */}
      <div className="m-sheet" role="dialog" aria-modal="true">
        <div className="m-sheet-grab" />
        <div className="m-sheet-head">
          <div>
            <div className="s-t">{title}</div>
            {sub != null && <div className="m-h-sub">{sub}</div>}
          </div>
          <button type="button" className="m-sheet-x" onClick={onClose} aria-label="Fechar">
            ✕
          </button>
        </div>
        <div className="m-sheet-body">{children}</div>
        {foot != null && <div className="m-sheet-foot">{foot}</div>}
      </div>
    </div>
  );
}

export interface HeaderProps {
  title?: ReactNode;
  sub?: ReactNode;
  onBack?: () => void;
  action?: ReactNode;
  ink?: boolean;
  left?: ReactNode;
}

/** Cabeçalho de tela: voltar/marca · título+sub · ação. */
export function Header({ title, sub, onBack, action, ink, left }: HeaderProps) {
  return (
    <div className={`m-header${ink ? " ink" : ""}`}>
      {onBack ? (
        <button type="button" className="m-back" onClick={onBack} aria-label="Voltar">
          <svg
            width="11"
            height="18"
            viewBox="0 0 11 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 1L2 9l7 8" />
          </svg>
        </button>
      ) : (
        left
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {title != null && <div className="m-h-title">{title}</div>}
        {sub != null && <div className="m-h-sub">{sub}</div>}
      </div>
      {action}
    </div>
  );
}
